/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **How a structured value goes out on a surface that cannot carry structure** —
 * D23 · R1/R3/R6, ratified 2026-08-18.
 *
 * `toWireScalar` (`wire-scalar.ts`) answers *can this value be represented here*, which is
 * knowable without asking anyone. This file answers the other half — *what should happen
 * when it cannot* — which is **not** knowable without asking, because dropping it,
 * flattening it, and sending JSON text are all defensible and which is right depends on
 * the receiver. Conflating the two questions is the defect the #1079 survey found at eight
 * sites, five of which answered the second with `String()`.
 *
 * So: capability is code, the rendering is **authored**, and the two live in separate
 * files on purpose.
 */

import type { DestinationFieldType } from './destination-field';
import { toWireScalar } from './wire-scalar';

/**
 * What a surface can carry — the **class**, not the surface (D23 · R1a).
 *
 * `scalar` is every surface that carries text: a query string, a request header, a pixel
 * URL. `structured` is a JSON leaf. Naming the class rather than enumerating surfaces is
 * deliberate and load-bearing: *"on anything that carries text, send this as CSV"* is one
 * statement an author can reason about, it stays correct when a ninth surface appears, and
 * it keeps a new surface from being a migration of every stored rendering.
 *
 * The **capability** hangs off the member in code and is never stored — a stored copy is a
 * second home free to disagree with the driver, the same shape as slice B's payload
 * duplication. The console needs no mirror of it either: it knows which editor it is
 * rendering, so *"can this row carry an object"* is answerable from being the ORTB body
 * editor rather than the parameter editor.
 */
export type WireSurfaceClass = 'scalar' | 'structured';

/** Whether a surface of this class carries structure natively. Code, never stored. */
export function surfaceCarriesStructure(surface: WireSurfaceClass): boolean {
  return surface === 'structured';
}

/**
 * How to render a value the surface cannot carry natively (D23 · R3, vocabulary by R6).
 *
 * **`csv` is deliberately absent, and its absence is the ruling.** It is not a rendering
 * but a name for the absence of a decision: it fixes no separator, no key policy, no
 * order and no nesting rule, so two operators choosing it against two exchanges get the
 * same string and at most one of them is right. Offering it would move R4's invented
 * flattening onto a menu item, where it acquires the authority of having been listed —
 * `AdServerPreset.endpointTemplate`'s rule in a second place: *a blank is obviously
 * unfinished; a wrong host silently no-bids and reads as the SSP's fault.*
 *
 * The test that produced this vocabulary: **is the output fully determined by the value?**
 * `omit`, `json` and `object` are — they take no parameters and nothing can disagree about
 * them. `join` is not, so it carries exactly the parameters the choice varies in, and no
 * more. Parameters rather than an expression because R3 requires *the editor shows the
 * rendered result*: an enumerable parameter set can be previewed and validated, and an
 * expression cannot — besides growing a serializer inside the mapping language, which is
 * the second type system #923/#940 already fight.
 */
export type WireRendering =
  /**
   * Send nothing **when the surface refuses the value**, deliberately. An **authored**
   * omission, and therefore not reported.
   *
   * Not a row-level off switch: a value the surface can carry is still carried. That is
   * `suppress`'s question, and answering it here too would put one concept on two controls.
   */
  | 'omit'
  /** JSON text. One rendering: escaping, nesting and key order are all fixed by the format. */
  | 'json'
  /** Pass the structure through. Legal only where {@link surfaceCarriesStructure} is true. */
  | 'object'
  | {
      kind: 'join';
      /**
       * Between members. Defaulted by the editor rather than assumed here — a default the
       * author can see and change is a different act from `csv` deciding silently.
       */
      separator: string;
      /**
       * Joins a key to its value. **Absent means values only** — folded into one field
       * rather than an `includeKeys` boolean beside a separator, so *"keys, with no
       * separator"* is unrepresentable rather than merely discouraged. Same move as
       * `forwardedForHeader` returning `{}` instead of an empty string.
       */
      pairWith?: string;
      /**
       * Object key order is *insertion* order, so one concept assembled by two code paths
       * can enumerate differently and produce two strings for one value. `alphabetical` is
       * how an author buys determinism when the receiver's parser cares.
       */
      order?: 'declared' | 'alphabetical';
    };

/**
 * Why a value did not go out. The distinction the caller needs is **authored or not**:
 * an omission the author asked for is a decision and must be silent, while one nobody
 * asked for is the #880 defect and must be reported (D23 · R2). Reporting both would
 * train the operator to ignore the warning that matters.
 */
export type WireRenderOmission =
  /** No rendering was authored and the value cannot be carried — R4's default. **Report.** */
  | 'unrendered'
  /** The author said `omit`. **Silent.** */
  | 'authored'
  /** A rendering was authored but the value nests below what it can flatten. **Report.** */
  | 'nested'
  /** `object` was chosen on a scalar surface — a validation finding. **Report.** */
  | 'not-representable-here';

export interface WireRenderResult {
  /** The value as the surface should carry it, or `undefined` if it goes out as nothing. */
  value?: string | number | boolean | object;
  /** Present exactly when {@link value} is absent. */
  omission?: WireRenderOmission;
}

/**
 * Narrow a stored `sendAs` coming off an untyped document (D23 · R3).
 *
 * **It rejects rather than repairs.** A malformed rendering must not degrade into a
 * different one — falling back to a join with some default separator would be the invented
 * flattening R6 exists to refuse, arriving through the parser.
 *
 * Shared rather than per-consumer because there are three: the driver rebuilds param fields
 * key by key, `MappingEntryDto.sendAs` has no normaliser of its own (which is how a
 * malformed value reaches {@link renderForWire} at all), and the console needs the same
 * answer when it reads a row it is about to offer for editing. Three copies of "is this a
 * valid rendering" is how one of them comes to accept something the wire refuses.
 */
export function normalizeWireRendering(raw: unknown): WireRendering | undefined {
  if (raw === 'omit' || raw === 'json' || raw === 'object') return raw;
  if (!raw || typeof raw !== 'object') return undefined;

  const candidate = raw as Record<string, unknown>;
  if (candidate.kind !== 'join' || typeof candidate.separator !== 'string') return undefined;

  const rendering: Extract<WireRendering, { kind: 'join' }> = {
    kind: 'join',
    separator: candidate.separator,
  };
  if (typeof candidate.pairWith === 'string') rendering.pairWith = candidate.pairWith;
  if (candidate.order === 'alphabetical' || candidate.order === 'declared') rendering.order = candidate.order;
  return rendering;
}

/**
 * Apply an authored rendering at the point a resolved value becomes an outbound one.
 *
 * **Keyed on what the row sends, not only on what the surface can hold** (`4f`, `#1416`). A surface
 * that carries structure is not a *slot* that accepts it: `imp.video.mimes` is a JSON leaf that takes a
 * list, `site.page` is a JSON leaf that takes a string. So `sends` is the row's sent type
 * (`sentType`: its own Type, else the slot's declaration, else the expression's yield), and it is
 * **required and nullable** — `null` says nothing declares one, never *I forgot to ask*.
 *
 * | | A single value | A flat list | A nested list or an object |
 * | --- | --- | --- | --- |
 * | **scalar surface** (query string, header) | sent | sent, joined (the IAB convention) | the rendering, else omitted and reported |
 * | **structured surface, row sends a list or an object** | sent | sent as it stands | sent as it stands |
 * | **structured surface, row sends a single value or nothing declared** | sent | the rendering, else joined | the rendering, else omitted and reported |
 *
 * **On a scalar surface a rendering governs only what the surface could not carry anyway.** A flat
 * list is carried there by convention, so a `join` on such a row is not an instruction to re-join
 * it. **On a structured surface the third row is different, and deliberately:** a JSON string slot
 * carries no list, so every rendering applies to a flat list too. Otherwise *Send structured values
 * as* would be offered on `site.page` and do nothing to the one value it was offered for. With no
 * rendering, a flat list is joined with commas there as well, which is what that leaf already sent
 * and what a query string sends for the same value.
 *
 * `json` always means JSON text, on every surface. **Dropping a row outright is `suppress`**, which is
 * a different question, has its own control, and is asked once rather than per rendering.
 */
export function renderForWire(
  value: unknown,
  rendering: WireRendering | undefined,
  surface: WireSurfaceClass,
  sends: DestinationFieldType | null,
): WireRenderResult {
  if (value === undefined || value === null) return {};

  const structured = surfaceCarriesStructure(surface);

  // A slot that takes a structure carries the value as it stands. `object` is the identity here and
  // needs no branch; an author who chose `json` on a JSON leaf asked for text and gets it.
  if (structured && (sends === 'array' || sends === 'object') && rendering !== 'json') {
    return { value: value as object };
  }

  const isStructure = Array.isArray(value) || (typeof value === 'object' && value !== null);
  // A single value is every surface's to carry.
  if (!isStructure) return { value: value as string | number | boolean };

  const native = toWireScalar(value);
  // A flat list on a text surface is carried by convention, whatever the rendering says.
  if (!structured && native !== undefined) return { value: native };

  if (rendering === undefined) {
    // A single-value JSON slot with no rendering sends a flat list joined, as it always has.
    return native !== undefined ? { value: native } : { omission: 'unrendered' };
  }
  // **`omit` is checked below the native guard, and that is the rule having no exception**
  // (2026-08-20). It used to be tested *first*, so "never send it" dropped a plain string and a flat
  // list as well — the only member that was not scoped to what the surface refuses. Two costs, and
  // the second is the real one: it made the control's label unwritable, and it gave the row a
  // *second* way to say "send nothing" beside the `suppress` checkbox.
  if (rendering === 'omit') return { omission: 'authored' };
  // `object` sends the structure, which a slot that takes a single value refuses.
  if (rendering === 'object') return { omission: 'not-representable-here' };
  if (rendering === 'json') return { value: JSON.stringify(value) };

  // **Anything unrecognised is unrendered, not a join.** Reaching `joinStructured` with a rendering
  // that is not one meant `separator` was `undefined` and `Array.join(undefined)` helpfully supplied a
  // comma — an invented flattening, arriving through a malformed stored value. `MappingEntryDto.sendAs`
  // has no normaliser, so this is reachable. Found by the console drift guard, which probes this
  // function for which members it implements rather than restating the list.
  if (typeof rendering !== 'object' || rendering.kind !== 'join' || typeof rendering.separator !== 'string') {
    return { omission: 'unrendered' };
  }

  const joined = joinStructured(value, rendering);
  return joined === undefined ? { omission: 'nested' } : { value: joined };
}

/**
 * Flatten one level, and **refuse to flatten two** (D23 · R6).
 *
 * Nesting is the point at which the parameter set stops being enumerable and `join`
 * becomes the serializer language R6 rejects, so a member that is itself structured makes
 * the whole value unrenderable rather than being silently stringified or skipped. That is
 * the same ruling `toWireScalar` already makes about a nested array — *a level of structure
 * discarded with no sign of it* is the same defect whichever direction it is discarded in,
 * and it is harder to notice than `[object Object]` because what comes out looks fine.
 */
function joinStructured(value: unknown, rendering: Extract<WireRendering, { kind: 'join' }>): string | undefined {
  const isScalar = (v: unknown): v is string | number | boolean =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

  if (Array.isArray(value)) {
    // A flat list reaches here only on a structured surface; a nested one never flattens.
    return value.every(isScalar) ? value.join(rendering.separator) : undefined;
  }

  if (typeof value !== 'object') return undefined;

  let entries = Object.entries(value as Record<string, unknown>);
  if (!entries.every(([, v]) => isScalar(v))) return undefined;

  if (rendering.order === 'alphabetical') {
    entries = [...entries].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  }

  return entries
    .map(([k, v]) => (rendering.pairWith === undefined ? String(v) : `${k}${rendering.pairWith}${String(v)}`))
    .join(rendering.separator);
}
